/* Order Explorer — the row-level view, under the dashboard's shared filter rail.

   Same /health/orders endpoint as the standalone Details page, and the columns are
   deliberately the same ones (curated from the confirmed response shape, NOT
   derived from Object.keys — a FAILED row has no awbNumber or labelUrl, and the
   newest row is usually a fresh failure, so key-sniffing silently drops columns).
   Adds a latency column, since the dashboard is the place people come to ask why a
   courier feels slow.

   `placementStatus` ("SUCCESS"/"FAILED") is the real outcome; `orderStatus` is a
   small int tracking-status code and is shown separately, not conflated with it. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Switch, Tooltip, Typography} from '@mui/material'
import {Link} from 'react-router-dom'
import {LoadErrorPanel} from '../../components'
import ProzoDataGrid from '../../../../common/ProzoDataGrid'
import CopyIcon from '../../../../common/CopyIcon'
import {usePcisHealthOrdersQuery} from '../../../../redux/supplyApiSlice'
import {AMBER, CORAL, Panel, Pill, TEAL, dedupeLatestByRef, money, ms, nf} from './parts'

/* Inline style, not sx: `.supply-scope a {color: inherit}` (0,1,1) beats emotion's
   generated class (0,1,0), so an sx-coloured link renders black in this scope. */
const linkStyle = {color: '#1d4ed8', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer'}

const fmtDateTime = (v) => {
  if (!v) return '—'
  const d = new Date(v)
  return isNaN(d) ? v : d.toLocaleString('en-IN', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'})
}

const muted = () => <Box sx={{fontSize: 12, color: 'text.disabled'}}>—</Box>

const COLUMNS = [
  {
    field: 'awbNumber',
    headerName: 'Courier & AWB',
    minWidth: 210,
    flex: 1.2,
    sortable: false,
    renderCell: ({row}) => (
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px', py: 0.5}}>
        {row.courierCode ? <Typography sx={{fontSize: 12.5, fontWeight: 700, lineHeight: 1.3}}>{row.courierCode}</Typography> : null}
        {row.awbNumber ? (
          <Box sx={{display: 'flex', alignItems: 'center', gap: '2px'}}>
            <Link
              to={`/order-detail/${row.awbNumber}?id=${row.id}`}
              target='_blank'
              rel='noopener noreferrer'
              style={{...linkStyle, fontSize: '12px', letterSpacing: '0.02em', textDecorationColor: '#93c5fd'}}
            >
              {row.awbNumber}
            </Link>
            <CopyIcon text={row.awbNumber} title='Copy AWB' color='#1d4ed8' />
          </Box>
        ) : (
          <Typography sx={{fontSize: 11, color: 'text.disabled'}}>No AWB</Typography>
        )}
      </Box>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    minWidth: 140,
    sortable: false,
    renderCell: ({row}) => <Typography sx={{fontSize: 12, lineHeight: 1.3}}>{fmtDateTime(row.createdAt)}</Typography>,
  },
  {
    field: 'placementStatus',
    headerName: 'Placement',
    minWidth: 105,
    sortable: false,
    renderCell: ({row}) =>
      row.placementStatus ? (
        <Pill palette={row.placementStatus === 'SUCCESS' ? TEAL : CORAL}>{row.placementStatus}</Pill>
      ) : (
        muted()
      ),
  },
  {
    field: 'courierApiDurationMs',
    headerName: 'Latency',
    minWidth: 92,
    sortable: false,
    renderCell: ({row}) =>
      row.courierApiDurationMs == null ? (
        <Tooltip title='Never reached the courier — rejected before the API call'>{muted()}</Tooltip>
      ) : (
        <Typography sx={{fontSize: 12, fontWeight: row.courierApiDurationMs >= 3000 ? 700 : 400, color: row.courierApiDurationMs >= 3000 ? CORAL.main : 'text.primary'}}>
          {ms(row.courierApiDurationMs)}
        </Typography>
      ),
  },
  {
    field: 'routingMode',
    headerName: 'Routing',
    minWidth: 105,
    sortable: false,
    renderCell: ({row}) =>
      row.routingMode ? (
        <Pill palette={row.routingMode === 'NATIVE' ? TEAL : AMBER}>{row.routingMode === 'NATIVE' ? 'PCIS' : 'Clickpost'}</Pill>
      ) : (
        muted()
      ),
  },
  {
    field: 'referenceNumber',
    headerName: 'Reference',
    minWidth: 165,
    flex: 1,
    sortable: false,
    renderCell: ({row}) =>
      row.referenceNumber ? (
        <Box sx={{display: 'flex', alignItems: 'center', gap: '2px'}}>
          <Typography sx={{fontSize: 12, lineHeight: 1.3, wordBreak: 'break-all'}}>{row.referenceNumber}</Typography>
          <CopyIcon text={row.referenceNumber} title='Copy reference' />
        </Box>
      ) : (
        muted()
      ),
  },
  {
    field: 'failureReason',
    headerName: 'Failure Reason',
    minWidth: 210,
    flex: 1.4,
    sortable: false,
    renderCell: ({row}) =>
      row.failureReason ? (
        <Tooltip title={row.failureReason}>
          <Typography sx={{fontSize: 12, color: '#b91c1c', lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>
            {row.failureReason}
          </Typography>
        </Tooltip>
      ) : (
        muted()
      ),
  },
  {
    field: 'orderType',
    headerName: 'Type',
    minWidth: 118,
    sortable: false,
    renderCell: ({row}) => (
      <Stack spacing={0.25}>
        <Typography sx={{fontSize: 12, fontWeight: 600, lineHeight: 1.2}}>{row.orderType || '—'}</Typography>
        <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>{row.deliveryType || ''}</Typography>
      </Stack>
    ),
  },
  {
    field: 'invoiceValue',
    headerName: 'Value',
    minWidth: 115,
    sortable: false,
    renderCell: ({row}) => (
      <Stack spacing={0.25}>
        <Typography sx={{fontSize: 12, fontWeight: 600, lineHeight: 1.2}}>{money(row.invoiceValue)}</Typography>
        {row.codValue ? <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>COD {money(row.codValue)}</Typography> : null}
      </Stack>
    ),
  },
  {
    field: 'pickupPincode',
    headerName: 'Route (PIN)',
    minWidth: 135,
    sortable: false,
    renderCell: ({row}) => (
      <Typography sx={{fontSize: 12, lineHeight: 1.3}}>
        {row.pickupPincode || '—'} <Box component='span' sx={{color: 'text.disabled'}}>→</Box> {row.dropPincode || '—'}
      </Typography>
    ),
  },
  {
    field: 'channelType',
    headerName: 'Channel',
    minWidth: 145,
    flex: 1,
    sortable: false,
    renderCell: ({row}) => (
      <Stack spacing={0.25}>
        <Typography sx={{fontSize: 12, fontWeight: 600, lineHeight: 1.2}}>{row.channelType || '—'}</Typography>
        {row.channelOrderId ? (
          <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2, wordBreak: 'break-all'}}>{row.channelOrderId}</Typography>
        ) : null}
      </Stack>
    ),
  },
  {
    field: 'accountCode',
    headerName: 'Account',
    minWidth: 165,
    flex: 1,
    sortable: false,
    renderCell: ({row}) => <Typography sx={{fontSize: 12, lineHeight: 1.3, wordBreak: 'break-all'}}>{row.accountCode || '—'}</Typography>,
  },
]

export default function TabOrders({filters, pollMs}) {
  const [page, setPage] = useState(0) // 0-based — matches both ProzoDataGrid and the API
  const [sizePerPage, setSizePerPage] = useState(20) // API caps size at 100
  const [onlyFailed, setOnlyFailed] = useState(false)

  const {data, isFetching, isError, error, refetch} = usePcisHealthOrdersQuery(
    {...filters, onlyFailed, page, size: sizePerPage},
    pollMs ? {pollingInterval: pollMs} : undefined
  )

  /* Dedupe is page-local: it collapses repeat failures on the same reference
     number down to the latest one within THIS page only. `total`/`rowCount`
     stay the server's raw counts, since the backend paginates on the
     undeduped rows — a retry that lands on the next page can't be merged in
     from here. */
  const rows = onlyFailed ? dedupeLatestByRef(data?.orders || []) : data?.orders || []
  /* Pagination is sized against whichever population is on screen: with "only
     failed" on, the grid must page through failures, not the whole window. */
  const total = onlyFailed ? data?.failedOrders ?? 0 : data?.totalOrders ?? 0

  const setFailedOnly = (v) => {
    setOnlyFailed(v)
    setPage(0)
  }

  if (isError) return <LoadErrorPanel message={error?.message || 'Could not load orders.'} onRetry={refetch} />

  return (
    <Panel
      title='Order explorer'
      note={
        <>
          {nf(data?.totalOrders ?? 0)} orders in this window · {nf(data?.failedOrders ?? 0)} failed. Every filter above applies.
        </>
      }
      action={
        <Stack direction='row' alignItems='center' spacing={0.5}>
          <Switch size='small' checked={onlyFailed} onChange={(e) => setFailedOnly(e.target.checked)} />
          <Typography sx={{fontSize: 12, fontWeight: 600, color: onlyFailed ? CORAL.main : 'text.secondary'}}>
            {onlyFailed ? 'Failed only' : 'All placements'}
          </Typography>
        </Stack>
      }
    >
      <ProzoDataGrid
        columns={COLUMNS}
        rows={rows}
        getRowId={(r) => r.id || r.referenceNumber}
        rowCount={total}
        page={page}
        setPage={setPage}
        sizePerPage={sizePerPage}
        setSizePerPage={(s) => {
          setSizePerPage(s)
          setPage(0)
        }}
        pagination
        loading={isFetching}
      />
      <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 1.5}}>
        A blank latency means the order never reached the courier — it was rejected by validation before the API call, so it is
        excluded from every percentile on this dashboard.
      </Typography>
    </Panel>
  )
}

TabOrders.propTypes = {filters: PropTypes.object.isRequired, pollMs: PropTypes.number}
